/**
 * Which one surface owns the chat area below lg: the conversations sheet, the
 * artifacts overlay, or neither.
 *
 * WHY A PURE DERIVATION (Codex rounds 3-4 on #2179). The sheet is z-20 and the
 * artifacts overlay z-30 (a layer cap an existing test pins), so if both were
 * rendered the overlay would paint over the sheet while focus sat inside it.
 * Keeping them apart with effects fails twice: an `artifactOpen` that stays true
 * across batched renders never re-runs the effect, and the effect only runs
 * AFTER a commit that already showed both. So the exclusion lives here, in what
 * is RENDERED, computed from state in the same render: an overlapping state
 * cannot be committed, not even for one frame.
 *
 * "Latest action wins" is made exact with a counter, not a flag: the store bumps
 * `artifactOpenSeq` on every openArtifact(), and the sheet remembers the value it
 * saw when it was opened. If the artifact has been opened since, the sheet is
 * stale and is not shown; opening the sheet records the current value, so it
 * supersedes any artifact opened before. Store-driven opens need no cooperation
 * from the page (no effect, no event).
 */
export type NarrowSurface = 'none' | 'sheet' | 'artifact';

export function resolveNarrowSurface({
  isWide,
  sheetOpenedAtSeq,
  artifactOpenSeq,
  artifactOpen,
}: {
  /** >= lg: the docked layout, where there is no narrow surface at all. */
  isWide: boolean;
  /** `artifactOpenSeq` as it was when the sheet was opened; null = not opened. */
  sheetOpenedAtSeq: number | null;
  /** The store's count of openArtifact() calls so far. */
  artifactOpenSeq: number;
  /** The store's artifact panel `open` flag. */
  artifactOpen: boolean;
}): NarrowSurface {
  if (isWide) return 'none';
  if (sheetOpenedAtSeq !== null && sheetOpenedAtSeq === artifactOpenSeq) {
    return 'sheet';
  }
  return artifactOpen ? 'artifact' : 'none';
}
